import type { Session } from "@supabase/supabase-js";

export const MOBILE_RECOVERY_HOST = "links.chickytutor.com";
export const MOBILE_RECOVERY_PATH = "/auth/mobile-reset-password";
export const MOBILE_RECOVERY_URL = `https://${MOBILE_RECOVERY_HOST}${MOBILE_RECOVERY_PATH}`;

export type RecoveryLink = { tokenHash: string | null };

/** Only our exact HTTPS destination is a recovery link. Never accept token sessions. */
export function parseMobileRecoveryLink(value: string): RecoveryLink | null {
  try {
    const url = new URL(value);
    if (
      url.origin !== `https://${MOBILE_RECOVERY_HOST}` ||
      url.pathname !== MOBILE_RECOVERY_PATH ||
      url.username ||
      url.password
    )
      return null;
    const fragment = new URLSearchParams(url.hash.slice(1));
    const token = fragment.get("token_hash");
    return {
      tokenHash:
        !url.search &&
        token &&
        fragment.toString() === `token_hash=${token}` &&
        /^[a-f0-9]{40,128}$/i.test(token)
          ? token
          : null,
    };
  } catch {
    return null;
  }
}

export type RecoveryError = "expired" | "failed" | "same" | "weak" | "busy";
export type RecoveryResult =
  | { error: RecoveryError; session?: never }
  | { error: null; session: Session };

function recoveryError(error: { code?: string; status?: number }): RecoveryError {
  if (
    ["otp_expired", "otp_disabled", "session_not_found", "refresh_token_not_found"].includes(
      error.code || ""
    )
  )
    return "expired";
  if (error.code === "same_password") return "same";
  if (error.code === "weak_password") return "weak";
  return "failed";
}

/** Pass a dedicated nonpersistent client, never the user's normal auth client. */
type AuthError = { code?: string; status?: number };
export type RecoveryAuth = {
  verifyOtp(params: {
    token_hash: string;
    type: "recovery";
  }): Promise<{ data: { session: Session | null }; error: AuthError | null }>;
  getSession(): Promise<{ data: { session: Session | null }; error: AuthError | null }>;
  updateUser(params: { password: string }): Promise<{ error: AuthError | null }>;
};

export function createMobileRecoveryAttempt(auth: RecoveryAuth, tokenHash: string) {
  let verified: Session | null = null;
  let busy = false;
  let finished = false;
  return {
    async save(password: string): Promise<RecoveryResult> {
      if (busy) return { error: "busy" };
      if (finished) return { error: "expired" };
      busy = true;
      try {
        if (!verified) {
          const result = await auth.verifyOtp({ token_hash: tokenHash, type: "recovery" });
          if (result.error) return { error: recoveryError(result.error) };
          if (!result.data.session) return { error: "expired" };
          verified = result.data.session;
        }
        const current = await auth.getSession();
        const session = current.data.session;
        if (
          current.error ||
          !session ||
          session.user.id !== verified.user.id ||
          session.access_token !== verified.access_token ||
          (session.expires_at && session.expires_at * 1000 <= Date.now())
        ) {
          finished = true;
          return { error: "expired" };
        }
        const result = await auth.updateUser({ password });
        if (result.error) return { error: recoveryError(result.error) };
        finished = true;
        return { error: null, session };
      } catch {
        return { error: "failed" };
      } finally {
        busy = false;
      }
    },
  };
}
